/**
 * @file LabelPrintItemRow.tsx
 * @description Строка диалога печати этикеток: отметка, фасовка и партия,
 *   статус этикеток, степпер количества и шаблон на эту печать (phase19.22,
 *   ui-design §1).
 *
 * Галочка — зона клика 56px (правила warehouse-UI), степпер тоже 56px.
 * Переопределение шаблона — только на эту печать: шаблон фасовки меняется в
 * карточке товара, здесь — «эту партию напечатать на мини».
 */

import { LabelQtyStepper } from "./LabelQtyStepper";
import { LabelStatusBadge } from "./LabelStatusBadge";
import { LABEL_TEXT } from "./label-texts";
import type { LabelRow } from "./useLabelPrintForm";
import { Checkbox } from "../../ui/checkbox";
import { SelectField } from "../../ui/SelectField";
import {
  LABEL_TEMPLATE_META,
  LABEL_TEMPLATE_ORDER,
  type LabelTemplate,
} from "../../../lib/api-labels";
import { formatMonthYear } from "../../../lib/receipt-expiry";
import { cn } from "@/lib/utils";

interface Props {
  row: LabelRow;
  /** Шаблон фасовки (без переопределения) — подпись «По фасовке (Мини)». */
  baseTemplate: LabelTemplate;
  onToggle: () => void;
  onQty: (qty: number) => void;
  onOverride: (template: LabelTemplate | null) => void;
}

/** «L0000042 · RCP-…-1 · до 03.2027» — партия строки одной фразой. */
function lotLine(row: LabelRow): string {
  const { item } = row;
  if (!item.lotId) return "Без партии";
  const parts = [item.lotCode, item.batchNumber, `до ${formatMonthYear(item.expiresAt)}`];
  return parts.filter(Boolean).join(" · ");
}

export function LabelPrintItemRow({ row, baseTemplate, onToggle, onQty, onOverride }: Props) {
  const { item } = row;
  const options = [
    { value: "", label: `${LABEL_TEXT.templateAuto} (${LABEL_TEMPLATE_META[baseTemplate].short})` },
    ...LABEL_TEMPLATE_ORDER.map((t) => ({ value: t, label: LABEL_TEMPLATE_META[t].title })),
  ];

  return (
    <li
      className={cn(
        "flex gap-2 rounded-xl p-2 ring-ghost",
        row.checked ? "bg-surface-lowest" : "bg-surface-low/60",
      )}
    >
      <label className="inline-flex size-14 shrink-0 cursor-pointer items-center justify-center">
        <Checkbox
          checked={row.checked}
          onCheckedChange={onToggle}
          aria-label={`Печатать: ${item.skuName}`}
          className="size-5"
        />
      </label>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <span className="min-w-0 line-clamp-2 text-sm font-medium">
              {item.lineNo ? `${item.lineNo}. ` : ""}
              {item.skuName}
            </span>
            {item.labelStatus ? <LabelStatusBadge status={item.labelStatus} variant="cell" /> : null}
          </div>
          <span className="truncate font-mono text-xs text-on-surface-variant">{lotLine(row)}</span>
        </div>
        {row.checked ? (
          <>
            <LabelQtyStepper
              value={row.qty}
              onChange={onQty}
              ariaLabel={`Этикеток: ${item.skuName}`}
            />
            <SelectField
              value={row.override ?? ""}
              onChange={(next) => onOverride(next ? (next as LabelTemplate) : null)}
              options={options}
              ariaLabel={`Шаблон этикетки: ${item.skuName}`}
            />
          </>
        ) : null}
      </div>
    </li>
  );
}
